import React, { useState, useEffect } from "react";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog, clearActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { Spin, Pagination } from "antd";
import dayjs from "dayjs";
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";

const INVOICE_FINANCING_CATEGORY_ID = "DCT1000001";

const DealActivityLog = ({
  dealId,
  orgId,
  dealName,
  dealCategoryId = INVOICE_FINANCING_CATEGORY_ID,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs, loading, totalRecords } = useSelector((state) => state.activityLog);

  useEffect(() => {
    setPage(1);
  }, [dealId, orgId]);

  useEffect(() => {
    if (orgId && dealId) {
      dispatch(
        getActivityLog({
          module: "BUSINESS",
          entityId: orgId,
          dealId,
          dealCategoryId: dealCategoryId || INVOICE_FINANCING_CATEGORY_ID,
          page,
          limit,
        })
      );
    }
  }, [dispatch, orgId, dealId, page, dealCategoryId]);

  useEffect(() => {
    return () => {
      dispatch(clearActivityLog());
    };
  }, [dispatch]);

  const formatPayloadValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object") return JSON.stringify(value);

    const strVal = String(value).trim();

    if (
      key === "approvedAmt" ||
      key === "invoiceAmount" ||
      key === "totalInvoiceValue" ||
      key === "fundRequired" ||
      key === "fundedAmt" ||
      key === "disbursedAmt" ||
      key === "settledAmt" ||
      key === "balanceAmt" ||
      key === "stampFee"
    ) {
      const num = Number(strVal.replace(/,/g, ""));
      if (!isNaN(num)) {
        return num.toLocaleString("en-IN");
      }
    }

    if (key === "manual" || key === "isDuplicate" || key === "fullySettled") {
      return strVal === "true" || value === true ? "Yes" : "No";
    }

    return strVal;
  };

  const filterPayloadEntries = (payload = {}) => {
    if (!payload || typeof payload !== "object") return [];

    return Object.entries(payload).filter(([key]) => {
      const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (
        lowerKey === "dealcategoryid" ||
        lowerKey === "dealcategoryname" ||
        lowerKey === "dealid"
      ) {
        return false;
      }
      return true;
    });
  };

  const formatPayloadKey = (key) => {
    if (key === "approvedAmt") return "Approved Amount";
    if (key === "invoiceAmount" || key === "totalInvoiceValue") {
      return isSupplyChainCategory(dealCategoryId) ? "Purchase Order Amount" : "Invoice Amount";
    }
    if (key === "fundRequired") return "Fund Required";
    if (key === "fundedAmt") return "Funded Amount";
    if (key === "disbursedAmt") return "Disbursed Amount";
    if (key === "settledAmt") return "Settled Amount";
    if (key === "balanceAmt") return "Balance Amount";
    if (key === "stampFee") return "Stamp Fee";
    if (key === "fullySettled") return "Fully Settled";
    if (key === "investorOrgId") return "Investor Org Id";
    if (key === "isDuplicate") return "Duplicate";
    if (key === "manual") return "Manual Disbursement";
    return key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
  };

  const getActionConfig = (action) => {
    switch (action) {
      case "DEAL_UPLOADED":
        return { label: "Deal Uploaded", color: "#e6f7ff", textColor: "#1890ff" };
      case "DEAL_APPROVED":
        return { label: "Deal Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "DEAL_REJECTED":
        return { label: "Deal Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "DEAL_FUNDED":
        return { label: "Deal Funded", color: "#fff7e6", textColor: "#fa8c16" };
      case "DEAL_PENDING_DISBURSEMENT":
        return { label: "Pending Disbursement", color: "#fffbe6", textColor: "#d4b106" };
      case "DEAL_DISBURSED":
        return { label: "Deal Disbursed", color: "#f9f0ff", textColor: "#722ed1" };
      case "DEAL_CANCELLED":
        return { label: "Deal Cancelled", color: "#fff0f6", textColor: "#eb2f96" };
      case "DEAL_REPUBLISHED":
        return { label: "Deal Republished", color: "#e6f7ff", textColor: "#1890ff" };
      case "DEAL_PARTIALLY_SETTLED":
        return { label: "Partially Settled", color: "#fff7e6", textColor: "#fa8c16" };
      case "DEAL_SETTLED":
        return { label: "Deal Settled", color: "#f6ffed", textColor: "#389e0d" };
      default:
        return {
          label: String(action || "").replace(/^DEAL_/, "").replace(/_/g, " "),
          color: "#e6f7ff",
          textColor: "#1890ff",
        };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const target =
      dealName || log.payload?.dealName || dealId || "this deal";

    if (log.action === "DEAL_UPLOADED") {
      return `<strong>${name}</strong> (${role}) <strong>UPLOADED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_FUNDED") {
      return `<strong>${name}</strong> (${role}) <strong>FUNDED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_PENDING_DISBURSEMENT") {
      return `<strong>${name}</strong> (${role}) <strong>MARKED PENDING DISBURSEMENT</strong> for deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_DISBURSED") {
      return `<strong>${name}</strong> (${role}) <strong>DISBURSED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_CANCELLED") {
      return `<strong>${name}</strong> (${role}) <strong>CANCELLED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_REPUBLISHED") {
      return `<strong>${name}</strong> (${role}) <strong>REPUBLISHED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_PARTIALLY_SETTLED") {
      return `<strong>${name}</strong> (${role}) <strong>PARTIALLY SETTLED</strong> deal <strong>"${target}"</strong>`;
    }
    if (log.action === "DEAL_SETTLED") {
      return `<strong>${name}</strong> (${role}) <strong>SETTLED</strong> deal <strong>"${target}"</strong>`;
    }

    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on deal <strong>"${target}"</strong>`;
  };

  if (!orgId || !dealId) {
    return (
      <div style={{ textAlign: "center", color: "#8c8c8c", padding: "40px 0" }}>
        Unable to load activity log for this deal.
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 0", maxWidth: "900px", margin: "0 auto", fontFamily: "Inter, sans-serif" }}>
      <div style={{ border: "1px solid #f0f0f0", borderRadius: "8px", overflow: "hidden", background: "#ffffff" }}>
        <div
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            background: "#fafafa",
            borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#262626" }}>
              Deal Lifecycle History
            </span>
            <span
              style={{
                background: "#e8e8e8",
                color: "#595959",
                fontSize: "12px",
                fontWeight: "600",
                padding: "2px 8px",
                borderRadius: "4px",
              }}
            >
              {totalRecords}
            </span>
          </div>
          <div>
            {isCollapsed ? (
              <DownOutlined style={{ fontSize: "14px", color: "#595959" }} />
            ) : (
              <UpOutlined style={{ fontSize: "14px", color: "#595959" }} />
            )}
          </div>
        </div>

        {!isCollapsed && (
          <div style={{ padding: "24px 24px 8px 24px" }}>
            <Spin spinning={loading}>
              {logs?.length === 0 ? (
                <div style={{ textAlign: "center", color: "#8c8c8c", padding: "20px 0" }}>
                  No activity logs found.
                </div>
              ) : (
                logs?.map((log, index) => {
                  const isLast = index === logs.length - 1;
                  const actionConfig = getActionConfig(log.action);
                  const dateStr = dayjs(log.createdAt).format("DD MMM YYYY");
                  const timeStr = dayjs(log.createdAt).format("hh:mm A");
                  const payloadEntries = filterPayloadEntries(log.payload);

                  return (
                    <div key={log.logId || index} style={{ display: "flex", minHeight: "80px" }}>
                      <div style={{ width: "90px", textAlign: "right", paddingRight: "20px", flexShrink: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: "500", color: "#262626" }}>{dateStr}</div>
                        <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>{timeStr}</div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "20px",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            border: `3px solid ${actionConfig.textColor}`,
                            background: "#ffffff",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            boxSizing: "border-box",
                          }}
                        >
                          <div
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: actionConfig.textColor,
                            }}
                          />
                        </div>

                        {!isLast && (
                          <div
                            style={{
                              width: "3px",
                              background: actionConfig.textColor,
                              flexGrow: 1,
                              marginTop: "4px",
                              marginBottom: "4px",
                            }}
                          />
                        )}
                      </div>

                      <div style={{ paddingLeft: "20px", paddingBottom: "24px", flexGrow: 1 }}>
                        <div
                          style={{ fontSize: "13px", color: "#434343", lineHeight: "1.5" }}
                          dangerouslySetInnerHTML={{ __html: formatDesc(log) }}
                        />

                        <div style={{ display: "flex", alignItems: "center", gap: "24px", marginTop: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Action:</span>
                            <span
                              style={{
                                background: actionConfig.color,
                                color: actionConfig.textColor,
                                fontSize: "11px",
                                fontWeight: "700",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                padding: "2px 8px",
                                borderRadius: "4px",
                              }}
                            >
                              {actionConfig.label}
                            </span>
                          </div>
                        </div>

                        {payloadEntries.length > 0 && (
                          <div
                            style={{
                              marginTop: 10,
                              padding: "10px 16px",
                              background: "#fafafa",
                              border: "1px solid #f0f0f0",
                              borderRadius: "6px",
                              fontSize: "12px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.01)",
                            }}
                          >
                            {payloadEntries.map(([key, value]) => (
                              <div key={key} style={{ marginBottom: "6px" }}>
                                <span style={{ fontWeight: "600", color: "#595959" }}>
                                  {formatPayloadKey(key)}:
                                </span>{" "}
                                <span style={{ color: "#262626" }}>
                                  {formatPayloadValue(key, value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </Spin>
            {totalRecords > limit && (
              <div style={{ textAlign: "right", marginTop: 10 }}>
                <Pagination
                  current={page}
                  total={totalRecords}
                  pageSize={limit}
                  onChange={(p) => setPage(p)}
                  size="small"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealActivityLog;
